import { useState, useRef } from 'react';
import { supabase } from '../supabase';
import CSVImport from './CSVImport';

const AMBER = '#D97706';
const AMBER_LIGHT = '#FEF3C7';
const AMBER_DARK = '#B45309';

// ── Industry configs ──────────────────────────────────────────────────────────

export const SHOP_CONFIGS = {
  machine_shop: {
    label: 'Machine Shop / CNC',
    code: 'CNC',
    icon: '⚙️',
    stages: ['Quote', 'PO Received', 'Setup', 'In Production', 'QC', 'Shipping', 'Invoiced'],
    templates: [
      { name: 'CNC Turning Part' },
      { name: 'Milled Aluminum Bracket' },
      { name: 'Precision Shaft' },
    ],
  },
  fabrication: {
    label: 'Fabrication / Welding',
    code: 'FAB',
    icon: '🔩',
    stages: ['Quote', 'PO Received', 'Material Procurement', 'Cutting', 'Welding', 'Finish / Grind', 'QC', 'Shipping', 'Invoiced'],
    templates: [
      { name: 'Steel Frame Assembly' },
      { name: 'Custom Bracket' },
      { name: 'Pipe Spool' },
    ],
  },
  sheet_metal: {
    label: 'Sheet Metal',
    code: 'SHT',
    icon: '🔧',
    stages: ['Quote', 'PO Received', 'Nesting', 'Laser / Punch', 'Forming', 'Hardware', 'QC', 'Shipping', 'Invoiced'],
    templates: [
      { name: 'Sheet Metal Enclosure' },
      { name: 'Formed Bracket Set' },
      { name: 'Laser Cut Panel' },
    ],
  },
  paint_finishing: {
    label: 'Paint / Finishing',
    code: 'FIN',
    icon: '🎨',
    stages: ['Quote', 'PO Received', 'Prep', 'Prime', 'Topcoat', 'QC', 'Shipping', 'Invoiced'],
    templates: [
      { name: 'Powder Coat Job' },
      { name: 'Wet Spray Finish' },
      { name: 'Anodize + Topcoat' },
    ],
  },
  general_manufacturing: {
    label: 'General Manufacturing',
    code: 'MFG',
    icon: '🏭',
    stages: ['Quote', 'PO Received', 'In Production', 'QC', 'Shipping', 'Invoiced'],
    templates: [
      { name: 'Custom Assembly' },
      { name: 'Prototype Build' },
      { name: 'Production Run' },
    ],
  },
  other: {
    label: 'Other',
    code: 'OTH',
    icon: '🔨',
    stages: ['Quote', 'PO Received', 'In Production', 'QC', 'Shipping', 'Invoiced'],
    templates: [
      { name: 'Custom Job' },
      { name: 'Repair / Rework' },
    ],
  },
};

const SHOP_TYPE_OPTIONS = [
  'machine_shop',
  'fabrication',
  'sheet_metal',
  'paint_finishing',
  'general_manufacturing',
  'other',
];

const DEFAULT_STAGES = SHOP_CONFIGS.general_manufacturing.stages;

// ── Component ─────────────────────────────────────────────────────────────────

export default function Onboarding({ session, companyId, companyName, onComplete }) {
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');

  // Step 2 — Shop type
  const [shopType, setShopType] = useState(null);

  // Step 3 — Stages
  const [stageNames, setStageNames] = useState(DEFAULT_STAGES);
  const [savingStages, setSavingStages] = useState(false);

  // Step 4 — Logo
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  // Step 5 — CSV Import
  const [savedStages, setSavedStages] = useState([]);
  const [importDone, setImportDone] = useState(false);

  const TOTAL_STEPS = 6;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSelectShopType = async (type) => {
    setError('');
    setShopType(type);
    const config = SHOP_CONFIGS[type];
    if (config) setStageNames(config.stages);
    setStep(3);
  };

  const handleSaveStages = async () => {
    setError('');
    const cleanedStages = stageNames.map((name) => name.trim()).filter(Boolean);
    if (!shopType || cleanedStages.length === 0) {
      setError('Choose a shop type and add at least one pipeline stage.');
      return;
    }
    if (new Set(cleanedStages.map((name) => name.toLowerCase())).size !== cleanedStages.length) {
      setError('Each pipeline stage needs a unique name.');
      return;
    }
    setSavingStages(true);
    const { data, error: setupError } = await supabase.rpc('configure_shop_onboarding', {
      p_company_id: companyId,
      p_user_id: session.user.id,
      p_shop_type: shopType,
      p_stage_names: cleanedStages,
      p_template_names: SHOP_CONFIGS[shopType]?.templates?.map((template) => template.name) ?? [],
    });
    if (setupError) {
      setError(`We could not save your workspace. ${setupError.message}`);
      setSavingStages(false);
      return;
    }
    setSavedStages(data?.stages ?? []);
    setSavingStages(false);
    setStep(4);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !companyId) return;
    setError('');
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 2 * 1024 * 1024) {
      setError('Use a PNG, JPG, or WebP logo no larger than 2 MB.');
      if (logoInputRef.current) logoInputRef.current.value = '';
      return;
    }
    setUploadingLogo(true);

    const ext = file.name.split('.').pop();
    const path = `company/${companyId}/logo.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('documents')
      .upload(path, file, { upsert: true });

    if (!uploadError) {
      const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(path);
      await supabase.from('companies').update({ logo_url: publicUrl }).eq('id', companyId);
      setLogoUrl(publicUrl);
    } else {
      setError(`We could not upload that logo. ${uploadError.message}`);
    }
    setUploadingLogo(false);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  // ── Progress bar ──────────────────────────────────────────────────────────

  const pct = Math.round((step / TOTAL_STEPS) * 100);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={s.overlay}>
      <div style={{ ...s.modal, ...(step === 5 ? s.modalWide : {}) }}>
        {/* Progress bar */}
        <div style={s.progressTrack}>
          <div style={{ ...s.progressFill, width: `${pct}%` }} />
        </div>

        {/* Step indicator */}
        <div style={s.stepIndicator}>
          Step {step} of {TOTAL_STEPS}
        </div>
        {error && <div role="alert" style={s.errorBanner}>{error}</div>}

        {/* ── Step 1: Welcome ── */}
        {step === 1 && (
          <div style={s.stepContent}>
            <div style={s.welcomeIcon}>TS</div>
            <h1 style={s.welcomeTitle}>
              Set up {companyName || 'your shop'}
            </h1>
            <p style={s.welcomeSubtitle}>
              Four decisions create your working pipeline. You can change any of them later.
            </p>
            <div style={s.stepsPreview}>
              {[
                { icon: '01', label: 'Choose your shop type' },
                { icon: '02', label: 'Review the production stages' },
                { icon: '03', label: 'Add your document logo' },
                { icon: '04', label: 'Review and import live jobs' },
              ].map((item, i) => (
                <div key={i} style={s.stepsPreviewItem}>
                  <span style={s.stepsPreviewIcon}>{item.icon}</span>
                  <span style={s.stepsPreviewLabel}>{item.label}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => setStep(2)}
              style={s.primaryBtn}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = AMBER_DARK)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = AMBER)}
            >
              Start setup
            </button>
          </div>
        )}

        {/* ── Step 2: Shop Type ── */}
        {step === 2 && (
          <div style={s.stepContent}>
            <h2 style={s.stepTitle}>What type of shop are you?</h2>
            <p style={s.stepSubtitle}>
              We'll pre-configure your pipeline stages and job templates to match your workflow.
            </p>

            <div style={s.shopTypeGrid}>
              {SHOP_TYPE_OPTIONS.map((type) => {
                const config = SHOP_CONFIGS[type];
                const isSelected = shopType === type;
                return (
                  <button
                    key={type}
                    onClick={() => handleSelectShopType(type)}
                    style={{
                      ...s.shopTypeBtn,
                      ...(isSelected ? s.shopTypeBtnSelected : {}),
                    }}
                  >
                    <span style={s.shopTypeIcon}>{config.code}</span>
                    <span style={s.shopTypeLabel}>{config.label}</span>
                  </button>
                );
              })}
            </div>

            <div style={s.btnRow}>
              <button onClick={() => setStep(1)} style={s.ghostBtn}>
                ← Back
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Configure Stages ── */}
        {step === 3 && (
          <div style={s.stepContent}>
            <h2 style={s.stepTitle}>Configure your pipeline stages</h2>
            <p style={s.stepSubtitle}>
              These stages are pre-filled for your shop type. Rename them to match the floor.
            </p>
            <div style={s.stageGrid}>
              {stageNames.map((name, idx) => (
                <div key={idx} style={s.stageRow}>
                  <span style={s.stageNum}>{idx + 1}</span>
                  <input
                    style={s.stageInput}
                    value={name}
                    onChange={(e) => {
                      const updated = [...stageNames];
                      updated[idx] = e.target.value;
                      setStageNames(updated);
                    }}
                    placeholder={`Stage ${idx + 1}`}
                  />
                </div>
              ))}
            </div>
            <div style={s.btnRow}>
              <button onClick={() => setStep(2)} style={s.ghostBtn}>
                ← Back
              </button>
              <button
                onClick={handleSaveStages}
                disabled={savingStages}
                style={{ ...s.primaryBtn, opacity: savingStages ? 0.7 : 1 }}
                onMouseEnter={(e) => { if (!savingStages) e.currentTarget.style.backgroundColor = AMBER_DARK; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = AMBER; }}
              >
                {savingStages ? 'Saving…' : 'Save stages'}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4: Upload Logo ── */}
        {step === 4 && (
          <div style={s.stepContent}>
            <h2 style={s.stepTitle}>Upload your company logo</h2>
            <p style={s.stepSubtitle}>
              Your logo appears on generated documents. PNG, JPG, or WebP up to 2 MB.
            </p>

            <div style={s.logoUploadBox}>
              {logoUrl ? (
                <img src={logoUrl} alt="Company logo" style={s.logoPreview} />
              ) : (
                <div style={s.logoPlaceholder}>
                  <span style={{ fontSize: 13, color: '#9CA3AF' }}>No logo uploaded</span>
                </div>
              )}
            </div>

            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              style={{ display: 'none' }}
              onChange={handleLogoUpload}
            />
            <button
              onClick={() => logoInputRef.current?.click()}
              disabled={uploadingLogo}
              style={{ ...s.outlineBtn, marginBottom: 8, opacity: uploadingLogo ? 0.7 : 1 }}
            >
              {uploadingLogo ? 'Uploading…' : logoUrl ? '↑ Replace Logo' : '↑ Upload Logo'}
            </button>

            <div style={s.btnRow}>
              <button onClick={() => setStep(3)} style={s.ghostBtn}>
                ← Back
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setStep(5)} style={s.ghostBtn}>
                  Skip
                </button>
                <button
                  onClick={() => setStep(5)}
                  style={s.primaryBtn}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = AMBER_DARK)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = AMBER)}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 5: Import Jobs ── */}
        {step === 5 && (
          <div style={s.stepContent}>
            {importDone ? (
              <div style={s.importSuccess}>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#059669' }}>
                  Jobs imported and verified.
                </p>
              </div>
            ) : (
              <div style={s.csvWrap}>
                <CSVImport
                  companyId={companyId}
                  stages={savedStages}
                  inline
                  onClose={() => {}}
                  onImported={() => setImportDone(true)}
                />
              </div>
            )}

            <style>{`
              .ts-ob-step5-btns {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-top: 8px;
                gap: 8px;
              }
              .ts-ob-step5-right {
                display: flex;
                gap: 8px;
              }
              @media (max-width: 600px) {
                .ts-ob-step5-btns {
                  flex-direction: column;
                  align-items: stretch;
                }
                .ts-ob-step5-right {
                  flex-direction: column;
                  width: 100%;
                }
                .ts-ob-step5-btns button {
                  width: 100% !important;
                  text-align: center;
                  justify-content: center;
                }
              }
            `}</style>
            <div className="ts-ob-step5-btns">
              <button onClick={() => setStep(4)} style={s.ghostBtn}>
                ← Back
              </button>
              <div className="ts-ob-step5-right">
                {!importDone && (
                  <button onClick={() => setStep(6)} style={s.ghostBtn}>
                    I'll add jobs manually
                  </button>
                )}
                <button
                  onClick={() => setStep(6)}
                  style={s.primaryBtn}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = AMBER_DARK)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = AMBER)}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 6: Done ── */}
        {step === 6 && (
          <div style={{ ...s.stepContent, textAlign: 'center' }}>
            <div style={s.doneIcon}>✓</div>
            <h2 style={s.stepTitle}>Workspace ready</h2>
            <p style={s.stepSubtitle}>
              {shopType && SHOP_CONFIGS[shopType]
                ? `Your ${SHOP_CONFIGS[shopType].label.toLowerCase()} dashboard is ready — your pipeline stages and job templates are pre-loaded.`
                : 'Your dashboard is ready. Start by creating your first job or explore the features.'}
            </p>
            <div style={s.doneChecks}>
              <DoneCheck label={`Shop type: ${shopType ? SHOP_CONFIGS[shopType]?.label : '—'}`} done={!!shopType} />
              <DoneCheck label="Pipeline stages configured" done={savedStages.length > 0} />
              <DoneCheck label="Logo uploaded" done={!!logoUrl} />
              <DoneCheck label="Jobs imported" done={importDone} />
            </div>
            <button
              onClick={onComplete}
              style={{ ...s.primaryBtn, marginTop: 28, fontSize: 15 }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = AMBER_DARK)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = AMBER)}
            >
              Open dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function DoneCheck({ label, done }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <span style={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        backgroundColor: done ? '#D1FAE5' : '#F3F4F6',
        color: done ? '#059669' : '#9CA3AF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 700,
        flexShrink: 0,
      }}>
        {done ? '✓' : '—'}
      </span>
      <span style={{ fontSize: 14, color: done ? '#374151' : '#9CA3AF' }}>{label}</span>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: 24,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: 8,
    width: '100%',
    maxWidth: 620,
    maxHeight: '94vh',
    overflowY: 'auto',
    boxShadow: '0 24px 80px rgba(0,0,0,0.28)',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  modalWide: {
    maxWidth: 1080,
  },
  progressTrack: {
    height: 4,
    backgroundColor: '#F3F4F6',
    borderRadius: '8px 8px 0 0',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: AMBER,
    borderRadius: 999,
    transition: 'width 0.4s ease',
  },
  stepIndicator: {
    padding: '12px 28px 0',
    fontSize: 12,
    fontWeight: 600,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  errorBanner: {
    margin: '12px 28px 0',
    padding: '10px 12px',
    border: '1px solid #FECACA',
    borderRadius: 6,
    color: '#991B1B',
    backgroundColor: '#FEF2F2',
    fontSize: 12,
    lineHeight: 1.45,
  },
  stepContent: {
    padding: '24px 28px 28px',
  },
  // Step 1
  welcomeIcon: {
    width: 44,
    height: 44,
    display: 'grid',
    placeItems: 'center',
    margin: '0 auto 16px',
    borderRadius: 7,
    backgroundColor: AMBER,
    color: '#fff',
    fontSize: 14,
    fontWeight: 800,
  },
  welcomeTitle: {
    margin: '0 0 12px',
    fontSize: 22,
    fontWeight: 800,
    color: '#111827',
    letterSpacing: 0,
    textAlign: 'center',
  },
  welcomeSubtitle: {
    margin: '0 0 24px',
    fontSize: 15,
    color: '#6B7280',
    lineHeight: 1.6,
    textAlign: 'center',
  },
  stepsPreview: {
    backgroundColor: '#FAFAF8',
    border: '1px solid #F3F4F6',
    borderRadius: 7,
    padding: '16px 20px',
    marginBottom: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  stepsPreviewItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  stepsPreviewIcon: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 10,
    fontWeight: 800,
    color: AMBER_DARK,
    width: 28,
    textAlign: 'center',
    flexShrink: 0,
  },
  stepsPreviewLabel: {
    fontSize: 14,
    color: '#374151',
    fontWeight: 500,
  },
  // Step 2 — Shop Type
  shopTypeGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    marginBottom: 20,
  },
  shopTypeBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 16px',
    backgroundColor: '#FAFAF8',
    border: '1px solid #E5E7EB',
    borderRadius: 7,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  shopTypeBtnSelected: {
    borderColor: AMBER,
    backgroundColor: AMBER_LIGHT,
  },
  shopTypeIcon: {
    width: 34,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 10,
    fontWeight: 800,
    color: AMBER_DARK,
    flexShrink: 0,
  },
  shopTypeLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#111827',
    lineHeight: 1.3,
  },
  // Step 3
  stepTitle: {
    margin: '0 0 8px',
    fontSize: 20,
    fontWeight: 800,
    color: '#111827',
    letterSpacing: 0,
  },
  stepSubtitle: {
    margin: '0 0 20px',
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 1.6,
  },
  stageGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 24,
  },
  stageRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  stageNum: {
    width: 28,
    height: 28,
    borderRadius: 5,
    backgroundColor: AMBER_LIGHT,
    color: AMBER_DARK,
    fontSize: 12,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  stageInput: {
    flex: 1,
    padding: '9px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: 8,
    fontSize: 14,
    color: '#111827',
    outline: 'none',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  // Step 4
  logoUploadBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 140,
    border: '2px dashed #E5E7EB',
    borderRadius: 7,
    marginBottom: 12,
    backgroundColor: '#FAFAF8',
  },
  logoPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  logoPreview: {
    maxWidth: 200,
    maxHeight: 100,
    objectFit: 'contain',
    borderRadius: 6,
  },
  // Step 5
  csvWrap: {
    border: 0,
    marginBottom: 20,
  },
  importSuccess: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    padding: '32px 16px',
    border: '1px solid #D1FAE5',
    borderRadius: 7,
    backgroundColor: '#F0FDF4',
    marginBottom: 20,
  },
  // Step 6
  doneIcon: {
    width: 46,
    height: 46,
    display: 'grid',
    placeItems: 'center',
    margin: '0 auto 16px',
    borderRadius: '50%',
    backgroundColor: '#047857',
    color: '#fff',
    fontSize: 20,
    fontWeight: 800,
  },
  doneChecks: {
    textAlign: 'left',
    marginTop: 20,
    padding: '16px 20px',
    backgroundColor: '#FAFAF8',
    border: '1px solid #F3F4F6',
    borderRadius: 7,
  },
  // Buttons
  primaryBtn: {
    padding: '11px 24px',
    backgroundColor: AMBER,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    whiteSpace: 'nowrap',
  },
  outlineBtn: {
    display: 'block',
    width: '100%',
    padding: '10px 20px',
    backgroundColor: 'transparent',
    color: '#374151',
    border: '1px solid #D1D5DB',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'border-color 0.15s ease',
  },
  ghostBtn: {
    padding: '11px 18px',
    backgroundColor: 'transparent',
    color: '#6B7280',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'border-color 0.15s ease',
  },
  btnRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
  },
};
